import * as d3 from "d3";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TrendingProjects } from "../../AllTokens";

interface TrendingProps {
  projects: TrendingProjects[];
  isLoading: boolean;
}

const normalizeValue = (
  value: number,
  minInput: number,
  maxInput: number,
  minOutput: number,
  maxOutput: number,
): number => {
  // Get absolute value for sizing, but keep sign for color
  const absValue = Math.abs(value);

  // Create scale function
  const scale = d3
    .scaleLinear() // Use log scale for better distribution of sizes
    .domain([minInput, maxInput]) // Input domain
    .range([minOutput, maxOutput])
    .clamp(true); // Clamp values to range

  return scale(absValue);
};

export const Trending: React.FC<TrendingProps> = ({ projects, isLoading }) => {
  const [viewMode, setViewMode] = useState<"list" | "bubble">("list");
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (viewMode === "bubble" && containerRef.current) {
      d3.select(containerRef.current).selectAll("*").remove();

      const minPriceChange = Math.min(
        ...projects.map((project) =>
          Math.abs(project.price.priceChange24HPercentage),
        ),
      );
      const maxPriceChange = Math.max(
        ...projects.map((project) =>
          Math.abs(project.price.priceChange24HPercentage),
        ),
      );

      const width = containerRef.current.offsetWidth;
      const height = containerRef.current.offsetHeight;

      const svg = d3
        .select(containerRef.current)
        .append("svg")
        .attr("width", width)
        .attr("height", height);

      // Create nodes data with smaller radius
      const nodes = projects.map((project) => ({
        ...project,
        radius: normalizeValue(
          project.price.priceChange24HPercentage,
          minPriceChange,
          maxPriceChange,
          20,
          80,
        ),
      }));

      // Adjusted force simulation for tighter packing
      const simulation = d3
        .forceSimulation(nodes as any)
        .force("center", d3.forceCenter(width / 2, height / 2))
        .force(
          "collide",
          d3
            .forceCollide()
            .strength(0.02)
            .radius(function (d) {
              return (d as any).radius + 2;
            }),
        )
        .on("tick", ticked);

      const node = svg
        .selectAll(".node")
        .data(nodes)
        .join("g")
        .attr("class", "node")
        .style("cursor", "pointer");

      // Add circles with token icons as patterns
      nodes.forEach((project, i) => {
        // Create pattern with both icon and overlay
        const defs = svg.append("defs");
        const pattern = defs
          .append("pattern")
          .attr("id", `pattern-${project.project.id}`)
          .attr("width", 1)
          .attr("height", 1)
          .attr("patternContentUnits", "objectBoundingBox");

        // Background color
        pattern
          .append("rect")
          .attr("width", 1)
          .attr("height", 1)
          .attr(
            "fill",
            project.price.priceChange24HPercentage > 0
              ? "#2E7D32aa"
              : "#F443361Aaa",
          );

        // Token icon
        pattern
          .append("image")
          .attr("width", 1)
          .attr("height", 1)
          .attr("opacity", "0.5")
          .attr(
            "xlink:href",
            ` https://mygateway.io/assets/img/tokens/${project.project.symbol}.webp` ||
              "",
          );

        const circle = node
          .filter((d, index) => index === i)
          .append("circle")
          .attr("r", project.radius)
          .style("fill", `url(#pattern-${project.project.id})`)
          .style(
            "stroke",
            project.price.priceChange24HPercentage > 0 ? "#4CAF50" : "#F44336",
          )
          .style("stroke-width", 2)
          .style("stroke-opacity", 0.5);

        // Hover effects
        circle
          .on("mouseover", function () {
            d3.select(this)
              .transition()
              .duration(200)
              .style("stroke-opacity", 1)
              .style("stroke-width", 2);
          })
          .on("mouseout", function () {
            d3.select(this)
              .transition()
              .duration(200)
              .style("stroke-opacity", 0.5)
              .style("stroke-width", 2);
          })
          .on("click", function () {
            navigate(`/report/${project.project.id}`);
          });

        // Add price change label
        node
          .filter((d, index) => index === i)
          .append("text")
          .text(
            `${project.price.priceChange24HPercentage > 0 ? "+" : ""}${Math.round(project.price.priceChange24HPercentage * 100)}%`,
          )
          .attr("text-anchor", "middle")
          .attr("dy", "0.3em")
          .attr(
            "fill",
            project.price.priceChange24HPercentage > 0 ? "#4CAF50" : "#F44336",
          )
          .style("font-size", "14px")
          .style("font-weight", "700")
          .style("pointer-events", "none") // Prevent text from interfering with hover
          .style("text-shadow", "1px 1px 1px #000000"); // Add text shadow
      });

      function ticked() {
        // Keep nodes within bounds
        node.attr("transform", (d: any) => {
          d.x = Math.max(d.radius, Math.min(width - d.radius, d.x));
          d.y = Math.max(d.radius, Math.min(height - d.radius, d.y));
          return `translate(${d.x},${d.y})`;
        });
      }

      return () => {
        simulation.stop();
      };
    }

    return () => {};
  }, [viewMode, projects, navigate]);

  return (
    <div className="w-full rounded-lg">
      <div className="w-full flex items-center justify-between mb-4">
        <div className="flex gap-4 items-center">
          <h2 className="text-white font-semibold">Trending</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode("bubble")}
              disabled={isLoading}
              className={`px-3 py-1 rounded ${
                viewMode === "bubble" ? "bg-blue-500" : "bg-gray-700"
              }`}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="white">
                <circle cx="12" cy="12" r="8" />
              </svg>
            </button>
            <button
              disabled={isLoading}
              onClick={() =>
                setViewMode(() => {
                  d3.select(containerRef.current).selectAll("*").remove();
                  return "list";
                })
              }
              className={`px-3 py-1 rounded ${
                viewMode === "list" ? "bg-blue-500" : "bg-gray-700"
              }`}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="white">
                <path d="M3 4h18v2H3V4zm0 7h18v2H3v-2zm0 7h18v2H3v-2z" />
              </svg>
            </button>
          </div>
        </div>

        {/* TODO  */}
        {/* <div className=" text-sm text-[#007DFF]">{"View all >"}</div> */}
      </div>

      {viewMode === "list" ? (
        <div className="overflow-hidden ">
          <table className="w-full">
            <thead>
              <tr>
                <th className="text-left text-gray-400 text-sm font-normal p-2 w-[40%]">
                  Token
                </th>
                <th className="text-right text-gray-400 text-sm font-normal p-2 w-[30%]">
                  Price
                </th>
                <th className="text-right text-gray-400 text-sm font-normal p-2 w-[30%]">
                  <span className="block xl:hidden">Score</span>
                  <span className="hidden xl:block">Gateway Score</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {projects
                .sort(
                  (a, b) =>
                    b.gatewayScore.gatewayScore - a.gatewayScore.gatewayScore,
                )
                .slice(0, 5)
                .map((project) => (
                  <tr
                    key={project.project.id}
                    className="hover:bg-[#ffffff0a] cursor-pointer border-solid border-b border-[rgba(255,255,255,0.05)] last:border-none"
                    onClick={() => navigate(`/report/${project.project.id}`)}
                  >
                    <td className="p-2 lg:p-1.5">
                      <div className="flex items-center gap-2">
                        <img
                          src={`https://mygateway.io/assets/img/tokens/${project.project.symbol}.webp`}
                          alt={project.project.name}
                          className="w-8 h-8 rounded-full"
                        />
                        <div>
                          <h3 className="text-white font-medium text-xs lg:text-sm">
                            {project.project.name}
                          </h3>
                          <p className="text-gray-400 text-xs lg:text-sm">
                            {project.project.symbol}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="p-2 lg:p-1.5">
                      <div className="text-right">
                        <p className="text-white text-xs lg:text-sm whitespace-nowrap">
                          {project.price.price.toFixed(4)} USD
                        </p>
                        <p
                          className={`text-xs lg:text-sm ${
                            project.price.priceChange24HPercentage > 0
                              ? "text-green-500"
                              : "text-red-500"
                          }`}
                        >
                          {project.price.priceChange24HPercentage > 0
                            ? "+"
                            : ""}
                          {(
                            project.price.priceChange24HPercentage * 100
                          ).toFixed(2)}
                          %
                        </p>
                      </div>
                    </td>
                    <td className="p-2 lg:p-1.5 text-right">
                      <div className="text-white text-lg font-semibold">
                        {project.gatewayScore.gatewayScore.toFixed(2)}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>

          {isLoading && projects.length === 0 && (
            <div className="flex justify-center p-4 w-full">
              <div className="flex justify-center items-center">
                <div className="animate-bounce inline-block h-2 w-2 bg-white rounded-full"></div>
                <div className="animate-bounce inline-block h-2 w-2 bg-white rounded-full ml-1"></div>
                <div className="animate-bounce inline-block h-2 w-2 bg-white rounded-full ml-1"></div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div ref={containerRef} className="h-[300px]  rounded-lg" />
      )}
    </div>
  );
};
